import React, { useCallback, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew';
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { useDispatch } from 'react-redux';
import { setLoading } from '@/store/slices/appSlice';
import { apiService } from '@/utils/api';
import { isVSDPump } from '@/utils/utils';
import type { AmbientTemp, Pump,Dashboard } from '@/types/dashboard';
import { getMotorStatusText, getMotorStatusColor } from '@/types/motorStatus';
import { FiSettings, FiSliders, FiActivity, FiThermometer, FiToggleRight, FiDroplet, FiChevronRight, FiEdit, FiRotateCcw } from 'react-icons/fi';


const SectionTitle: React.FC<{ title: string }>=({ title }) => (
  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
);

// Define typed shape of sensorsCalibration data payload
type SensorsCalibrationData = {
  ambientTemp: AmbientTemp;
  dashboardInfo: Dashboard;
  pumpInfo: Pump;
};

// Sidebar component
import PumpCalibrationSidebar from '@/components/calibration/PumpCalibrationSidebar';
import PhCalibrationSidebar from '@/components/calibration/PhCalibrationSidebar';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import TextField from '@mui/material/TextField';

const SensorsCalibrationPage: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();
  const [data, setData] = React.useState<SensorsCalibrationData | null>(null);
  const [pumpType, setPumpType] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isSidebarOpen, setSidebarOpen] = React.useState<boolean>(false);
  const [isPhSidebarOpen, setPhSidebarOpen] = React.useState<boolean>(false);
  const [calibrationModal, setCalibrationModal] = React.useState<{ target: 'ph' | 'orp' | 'water' | 'ambient', action: 'modify' | 'reset' } | null>(null);
  const [inputValue, setInputValue] = React.useState<string>('');

  const fetchData = useCallback(async (deviceId: string) => {
    dispatch(setLoading(true));
    setError(null);
    try {
      const res = await apiService.get<any>(`/api/v1/iot-shadow/device/${deviceId}/sensorsCalibration`);
      const payload: SensorsCalibrationData | null = res ?? null;
      setData(payload);
      const inferredPumpType = payload?.pumpInfo?.pump_type ?? null;
      setPumpType(inferredPumpType);
    } catch (err: any) {
      console.error('Failed to fetch sensors calibration:', err);
      setError(err?.message || 'Failed to fetch sensors calibration');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!selectedDeviceId) return;
    fetchData(selectedDeviceId);
  }, [selectedDeviceId, fetchData]);

  const isVsd = useMemo(() => isVSDPump(pumpType ?? ''), [pumpType]);

  const handleDeviceChange = useCallback((deviceId: string) => {
    onDeviceChange(deviceId);
  }, [onDeviceChange]);

  // Map values strictly from sensorsCalibration.data
  const pumpInfo = data?.pumpInfo ?? null;
  const pumpSpeed = pumpInfo?.actual_speed;
  const flowRate = pumpInfo?.actual_flowrate;
  const motorStatus = pumpInfo?.motor_status ?? 0;
  const actualPhRaw = data?.dashboardInfo?.actual_ph ?? null;
  const actualOrpRaw = data?.dashboardInfo?.actual_orp ?? null;
  const waterTempRaw = data?.ambientTemp?.water_temp ?? null;
  const ambientTempRaw = data?.ambientTemp?.ambient_temp ?? null;
  // NEW: offsets for calibration
  const waterTempOffsetRaw = data?.ambientTemp?.water_temp_offset ?? null;
  const ambientTempOffsetRaw = data?.ambientTemp?.ambient_temp_offset ?? null;

  // Enhanced display helpers
  const phValue = actualPhRaw != null ? actualPhRaw / 10 : null;

  const orpValue = actualOrpRaw != null ? actualOrpRaw : null;
  // NEW: helper values for temperature
  const waterTempValue = waterTempRaw != null ? waterTempRaw / 10 : null;
  const ambientTempValue = ambientTempRaw != null ? ambientTempRaw / 100 : null;

  // NEW: apply calibration for water/ambient temp
  const applyTemperatureCalibration = useCallback(async (target: 'water' | 'ambient', measuredValue: number) => {
    if (!selectedDeviceId) return;
    try {
      dispatch(setLoading(true));
      if (target === 'water') {
        const current = waterTempValue;
        if (current == null) throw new Error('No water temperature available');
        const currentOffset = waterTempOffsetRaw ?? 0;
        const delta = Math.round((measuredValue - current) * 10);
        const payload = { water_temp_offset: currentOffset + delta };
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setWaterTemp`, payload);
      } else {
        const current = ambientTempValue;
        if (current == null) throw new Error('No ambient temperature available');
        const currentOffset = ambientTempOffsetRaw ?? 0;
        const delta = Math.round((measuredValue - current) * 100);
        const payload = { ambient_temp_offset: currentOffset + delta };
        console.log('ambient currentOffset:', currentOffset, 'measuredValue:', measuredValue, 'current:', current, 'delta:', delta,"payload:", payload);

        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setAmbientTemp`, payload);
      }
      await fetchData(selectedDeviceId);
    } catch (err: any) {
      console.error('Calibration error:', err);
      setError(err?.message || 'Calibration failed');
    } finally {
      dispatch(setLoading(false));
    }
  }, [selectedDeviceId, dispatch, fetchData, waterTempValue, ambientTempValue, waterTempOffsetRaw, ambientTempOffsetRaw]);

  // NEW: reset calibration for water/ambient temp
  const resetTemperatureCalibration = useCallback(async (target: 'water' | 'ambient') => {
    if (!selectedDeviceId) return;
    try {
      dispatch(setLoading(true));
      const endpoint = target === 'water'
        ? `/api/v1/iot-shadow/device/${selectedDeviceId}/resetWaterTemp`
        : `/api/v1/iot-shadow/device/${selectedDeviceId}/resetAmbientTemp`;
      await apiService.post<{ message: string }>(endpoint, {});
      await fetchData(selectedDeviceId);
    } catch (err: any) {
      console.error('Reset calibration error:', err);
      setError(err?.message || 'Reset calibration failed');
    } finally {
      dispatch(setLoading(false));
    }
  }, [selectedDeviceId, dispatch, fetchData]);

  return (
    <div className="page-container relative flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 ">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sensors/Calibration</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">View calibration options and sensor readings</p>
          </div>
          <div className="flex items-center gap-2">
            <DeviceSelector
              onDeviceChange={handleDeviceChange}
              selectedDeviceId={selectedDeviceId}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="h-0.5 bg-gray-200 dark:bg-gray-700 my-4"></div>

        {/* Pump Calibration */}
        <Card>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <SectionTitle title="Pump Calibration" />
              <button
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open Pump Calibration"
              >
                <FiSettings className="text-xl" />
              </button>
            </div>
            {!isVsd && (
              <div className="px-3 py-2 rounded-md bg-amber-100 text-amber-700 text-sm">Flow calibration is not supported for non‑VSD pumps. Displaying pump information only.</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg">
                <FiSettings className="text-xl text-primary-500" />
                <div>
                  <p className="text-sm text-gray-500">{pumpInfo?.pump_type ?? pumpType ?? '-'} - {pumpInfo?.pump_brand} {pumpInfo?.pump_model}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg">
                <FiSliders className="text-xl text-primary-500" />
                <div>
                  <p className="font-medium">Pump Speed</p>
                  <p className="text-sm text-gray-500">{pumpSpeed != null ? `${pumpSpeed}%` : '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/50 rounded-lg">
                <FiActivity className="text-xl text-primary-500" />
                <div>
                  <p className="font-medium">Flow Rate</p>
                  <p className="text-sm text-gray-500">{flowRate != null ? `${flowRate} lpm` : '-'}</p>
                </div>
              </div>
              <div className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30">
                  <FiToggleRight className="h-5 w-5 text-green-600 dark:text-green-400" />
                </span>
                <div className="ml-3">
                  <div className="text-sm font-medium">{isVsd ? 'VSD Pump' : 'Standard Pump'}</div>
                  <div className={`text-xs ${getMotorStatusColor(motorStatus)}`}>{getMotorStatusText(motorStatus)}</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* pH & ORP Calibration in one row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SectionTitle title="pH Calibration" />
                  <FiDroplet className="text-xl text-primary-500" />
                </div>
                <button
                  className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
                  onClick={() => setPhSidebarOpen(true)}
                  aria-label="Open pH Calibration"
                >
                  <FiSettings className="text-xl" />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{phValue != null ? phValue.toFixed(1) : '-'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setInputValue(phValue != null ? String(phValue.toFixed(1)) : ''); setCalibrationModal({ target: 'ph', action: 'modify' }); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-xs"
                  >
                    <FiEdit className="text-sm" />
                    Modify
                  </button>
                  <button
                    onClick={() => setCalibrationModal({ target: 'ph', action: 'reset' })}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                  >
                    <FiRotateCcw className="text-sm" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SectionTitle title="ORP Calibration" />
                  <FiActivity className="text-xl text-primary-500" />
                </div>
                <FiSettings className="text-xl text-primary-600" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{orpValue != null ? `${orpValue} Mv` : '-'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setInputValue(orpValue != null ? String(orpValue) : ''); setCalibrationModal({ target: 'orp', action: 'modify' }); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-xs"
                  >
                    <FiEdit className="text-sm" />
                    Modify
                  </button>
                  <button
                    onClick={() => setCalibrationModal({ target: 'orp', action: 'reset' })}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                  >
                    <FiRotateCcw className="text-sm" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Temperature Calibration */}
        <Card>
          <div className="p-4 space-y-2">
            <SectionTitle title="Temperature Calibration" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Water Temperature */}
              <div className="p-4 bg-blue-50/70 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <FiThermometer className="text-3xl text-blue-500" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Water</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{waterTempValue != null ? `${waterTempValue.toFixed(1)}°C` : '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setInputValue(waterTempValue != null ? String(waterTempValue.toFixed(1)) : ''); setCalibrationModal({ target: 'water', action: 'modify' }); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-xs"
                    >
                      <FiEdit className="text-sm" />
                      Modify
                    </button>
                    <button
                      onClick={() => setCalibrationModal({ target: 'water', action: 'reset' })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                    >
                      <FiRotateCcw className="text-sm" />
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {/* Ambient Temperature */}
              <div className="p-4 bg-orange-50/70 dark:bg-orange-900/20 rounded-lg">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <FiThermometer className="text-3xl text-orange-500" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Ambient</p>
                      <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{ambientTempValue != null ? `${ambientTempValue.toFixed(1)}°C` : '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setInputValue(ambientTempValue != null ? String(ambientTempValue.toFixed(1)) : ''); setCalibrationModal({ target: 'ambient', action: 'modify' }); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-xs"
                    >
                      <FiEdit className="text-sm" />
                      Modify
                    </button>
                    <button
                      onClick={() => setCalibrationModal({ target: 'ambient', action: 'reset' })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                    >
                      <FiRotateCcw className="text-sm" />
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Calibration Dialog */}
      {calibrationModal && (
        <ConfirmDialog
          open={true}
          title={calibrationModal.action === 'modify' ? `Modify ${calibrationModal.target.toUpperCase()} Calibration` : `Reset ${calibrationModal.target.toUpperCase()} Calibration`}
          onCancel={() => setCalibrationModal(null)}
          onOk={async () => {
            if (!calibrationModal) return;
            if (calibrationModal.action === 'modify') {
              const val = parseFloat(inputValue);
              if (!Number.isNaN(val)) {
                if (calibrationModal.target === 'water' || calibrationModal.target === 'ambient') {
                  await applyTemperatureCalibration(calibrationModal.target, val);
                }
              }
            } else {
              if (calibrationModal.target === 'water' || calibrationModal.target === 'ambient') {
                await resetTemperatureCalibration(calibrationModal.target);
              }
            }
            setCalibrationModal(null);
          }}
          cancelText="Cancel"
          okText={calibrationModal.action === 'modify' ? 'Apply' : 'Confirm Reset'}
          disableOk={calibrationModal.action === 'modify' && inputValue.trim() === ''}
          fullWidth
          maxWidth="sm"
        >
          {calibrationModal.action === 'modify' ? (
            <>
              <div className="mb-3 p-3 rounded-md bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700 text-sm">
                Please ensure your filter pump is on and probe reading has stabilised before calibrating.
              </div>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                label={calibrationModal.target === 'ph' ? 'Measured pH' : calibrationModal.target === 'orp' ? 'Measured ORP (mV)' : calibrationModal.target === 'water' ? 'Measured Water Temperature (°C)' : 'Measured Ambient Temperature (°C)'}
                type="number"
                inputProps={{ step: calibrationModal.target === 'orp' ? 1 : 0.1 }}
                placeholder={calibrationModal.target === 'ph' ? 'e.g. 7.00' : calibrationModal.target === 'orp' ? 'e.g. 650' : 'e.g. 25.0'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </>
          ) : (
            <div className="text-sm text-gray-700 dark:text-gray-300">This will reset calibration offset for {calibrationModal.target.toUpperCase()}. Continue?</div>
          )}
        </ConfirmDialog>
      )}

      {/* Sidebar mount */}
      {isSidebarOpen && selectedDeviceId && (
        <PumpCalibrationSidebar
          deviceId={selectedDeviceId}
          isVsd={isVsd}
          onClose={() => setSidebarOpen(false)}
        />
      )}
      {isPhSidebarOpen && selectedDeviceId && (
        <PhCalibrationSidebar
          deviceId={selectedDeviceId}
          onClose={() => setPhSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SensorsCalibrationPage;